import LoadingPlaceHolder from '@/components/elements/LoadingPlaceholder';
import { useAppSelector } from '@/hooks';
import { useHandlePopstate } from '@/hooks/useHandlePopState';
import { BookState } from '@/types/state/book';
import { Place } from '@/types/state/shared';
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  getPickedEmployee,
  getScreenView,
  getUrlParameters,
  setUrlState,
  syncBookstateWithUrl,
} from './BookAppointment.helpers';
import { ModalView, View } from './BookAppointment.types';

function updateUrlState(
  place: Place,
  history: ReturnType<typeof useHistory>,
  location: ReturnType<typeof useLocation>,
) {
  return (
    updated:
      | { type: 'employee'; externalId: string }
      | { type: 'mainServices'; serviceIds: number[] }
      | { type: 'addOnServices'; serviceIds: number[] },
  ) => {
    /**
     * dont update url state if bookAppointment is not have a set place
     */
    if (!place) return;

    if (updated.type === 'employee') {
      setUrlState({ sr: updated.externalId ?? null }, history, location);
    }
    if (updated.type === 'mainServices') {
      setUrlState({ ss: updated.serviceIds?.join(',') ?? null }, history, location);
    }

    if (updated.type === 'addOnServices') {
      setUrlState({ rs: updated.serviceIds?.join(',') ?? null }, history, location);
    }
  };
}

const useBookAppointmentManager = (
  place?: Place,
): {
  view: ModalView;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  updateView: (view: View) => void;
  updateUrlState: ReturnType<typeof updateUrlState>;
} => {
  /**
   * Initial screen depends on if the user has picked a service or an employee
   * - if the user picks a service, and there is more than one employee && the place does not have hideEmployee setting, show the pickEmployee screen
   * - if the user picks a employee, show the pickServices screen
   */
  const bookstate = useAppSelector((state) => state.book) as BookState;

  const [view, setView] = useState<ModalView>(getScreenView(bookstate, place));
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const mountedRef = useRef<boolean>(false);
  const popstate = useHandlePopstate();
  const navigation = useLocation();

  const updateView = useCallback((next: View) => {
    setView((prev) => ({ current: next, previous: prev.current }));
  }, []);

  const open = useCallback(() => {
    if (view.current === undefined) {
      setView(getScreenView(bookstate, place, view.current));
    }
    setIsOpen(true);
  }, [bookstate, place, view]);

  const close = () => {
    setIsOpen(false);
    if (!mountedRef.current) return;
    setUrlState({ sr: null, ss: null, rs: null }, history, navigation);
  };

  useEffect(() => {
    if (view.current === undefined) {
      setView(getScreenView(bookstate, place, view.current));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookstate.services, bookstate.employee]);

  useEffect(
    () => {
      if (!place) return;
      /**
       * If the url has been updated by the browser navigation
       * change the input values to match the url and perform a new search
       * to sync the search results with the url
       */
      let shouldSyncState = false;
      const selectedEmployee = getPickedEmployee(bookstate.employee, place?.employees || [])?.about?.externalId;
      const selectedServices = (bookstate.services || [])?.map((s) => s.id).join(',');
      const selectedRecommendedServices = (bookstate.services || [])
        .filter((s) => s?.about?.settings?.isAddOn)
        .map((s) => s.id)
        .join(',');

      const urlState = getUrlParameters();

      if (popstate.didUpdate) {
        if (selectedEmployee !== urlState.sr) {
          shouldSyncState = true;
        }

        if (selectedServices !== urlState.ss) {
          shouldSyncState = true;
        }

        if (selectedRecommendedServices !== urlState.rs) {
          shouldSyncState = true;
        }

        if (shouldSyncState) {
          syncBookstateWithUrl(urlState, bookstate, place, dispatch, { open, close, view, updateView });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [popstate.didUpdate, navigation],
  );

  useEffect(() => {
    mountedRef.current = true;
    const urlState = getUrlParameters();

    if (place) {
      syncBookstateWithUrl(urlState, bookstate, place, dispatch, { open, close, view, updateView });
    }

    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    view,
    isOpen,
    open,
    close,
    updateView,
    updateUrlState: updateUrlState(place, history, navigation),
  };
};

export type BookAppointmentContext = ReturnType<typeof useBookAppointmentManager>;

export const BookAppointmentContext = createContext<BookAppointmentContext>({
  view: { current: 'summary' },
  isOpen: false,
  open: () => null,
  close: () => null,
  updateView: () => null,
  updateUrlState: () => null,
});

export const useBookAppointmentContext = () => {
  const context = useContext(BookAppointmentContext);

  if (!context) {
    throw new Error('useCheckout must be used within a CheckoutProvider');
  }

  return context;
};

export const BookAppointmentProvider: React.FC<{ children: ReactNode; place?: Place }> = ({ children, place }) => {
  const { show, loadSource } = useAppSelector((state) => state.loading);

  const manager = useBookAppointmentManager(place);

  return (
    <BookAppointmentContext.Provider value={manager}>
      {show && loadSource === 'fetchPlaceDetails' && (
        <div className="fixed inset-0 z-10">
          <LoadingPlaceHolder className="flex items-center justify-center" />
        </div>
      )}
      {children}
    </BookAppointmentContext.Provider>
  );
};
